import {
  Dialog,
  DialogVariant,
  LinkButton,
  TextHighlight,
  TextWeight,
  textStyleUtils
} from '@focaldata/cin-ui-components'
import { useState } from 'react'
import { DraftQuestionnaireEntry } from '../../../data/model/questionnaire'
import { useHasEarlierWaves } from './HasEarlierWavesContext'
import { useKpi } from './KpiProvider'
import useStyles from './KpiSummaryBox.styles'
import { getKpiName } from './kpi.utils'
import { describeKpiRemoval } from './kpiImpact'
import { getKpiRuleText, toKpiRuleInput } from './kpiRuleText'

interface Props {
  entry: DraftQuestionnaireEntry
}

const KpiSummaryBox = ({ entry }: Props) => {
  const kpi = useKpi()
  const { classes, cx } = useStyles()
  const { classes: textClasses } = textStyleUtils.useTextStyles()
  const hasEarlierWaves = useHasEarlierWaves()
  const [pendingRemoval, setPendingRemoval] = useState<{
    kpiId: string
    message: string
  }>()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  if (!kpi || kpi.kpis.length === 0) {
    return null
  }

  const { kpis, isSaving, openKpiDialog, removeKpi } = kpi
  const closeConfirm = () => {
    if (!isSaving) setIsConfirmOpen(false)
  }
  const isPendingKpiListed = kpis.some(
    ({ kpiId }) => kpiId === pendingRemoval?.kpiId
  )

  return (
    <div
      ref={kpi.summaryRef}
      tabIndex={-1}
      className={cx('fd-grid fd-container fd-column', classes.container)}
      data-testid="kpi-summary-box"
    >
      {kpis.map((item, index) => {
        const name = getKpiName(item, index, kpis.length)
        return (
          <div
            key={item.kpiId}
            className="fd-grid fd-container fd-row fd-justify-content-between fd-align-items-flex-start"
          >
            <p
              className={cx(
                'fd-grid fd-item fd-xs-10',
                textClasses.default,
                textClasses.sizeMs
              )}
            >
              <span className={textClasses.weightSemiBold}>{name}:</span>{' '}
              {getKpiRuleText(toKpiRuleInput(item, entry))}
            </p>
            <div
              className={cx(
                'fd-grid fd-container fd-item fd-xs-2 fd-justify-content-flex-end',
                classes.actions
              )}
            >
              <div className={classes.edit}>
                <LinkButton
                  highlight={TextHighlight.Background}
                  weight={TextWeight.SemiBold}
                  aria-label={`Edit KPI: ${name}`}
                  disabled={isSaving}
                  onClick={() => {
                    openKpiDialog({ kind: 'edit', kpiId: item.kpiId })
                  }}
                >
                  Edit
                </LinkButton>
              </div>
              <LinkButton
                highlight={TextHighlight.Background}
                weight={TextWeight.SemiBold}
                aria-label={`Remove KPI: ${name}`}
                disabled={isSaving}
                onClick={() => {
                  setPendingRemoval({
                    kpiId: item.kpiId,
                    message: describeKpiRemoval(kpis, item, hasEarlierWaves)
                  })
                  setIsConfirmOpen(true)
                }}
              >
                Remove
              </LinkButton>
            </div>
          </div>
        )
      })}
      {kpi.canAddQuestionKpi && (
        <LinkButton
          className={classes.add}
          highlight={TextHighlight.Primary}
          weight={TextWeight.SemiBold}
          noPadding
          onClick={() => {
            openKpiDialog({ kind: 'question' })
          }}
        >
          Add another KPI
        </LinkButton>
      )}
      <Dialog
        title="Remove this KPI?"
        open={isConfirmOpen && isPendingKpiListed}
        onClose={closeConfirm}
        onClickOutside={closeConfirm}
        variant={DialogVariant.DoubleButton}
        primaryButtonText="Remove KPI"
        primaryButtonClick={async () => {
          if (pendingRemoval && (await removeKpi(pendingRemoval.kpiId))) {
            setIsConfirmOpen(false)
          }
        }}
        primaryButtonLoading={isSaving}
        secondaryButtonText="Cancel"
        secondaryButtonClick={closeConfirm}
        secondaryButtonDisabled={isSaving}
      >
        <p className={cx(textClasses.default, textClasses.weightSemiBold)}>
          {pendingRemoval?.message}
        </p>
      </Dialog>
    </div>
  )
}

export default KpiSummaryBox
